import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Grid,
  Divider,
  Chip,
  Alert,
  CircularProgress,
  InputAdornment,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  SelectChangeEvent
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  Calculate as CalculateIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import * as paymentMethodService from '../../services/paymentMethodService';
import { IPaymentMethodUsage, IFeesCalculation } from '../../types/paymentMethod';

interface PaymentMethodsSectionProps {
  invoiceTotal: number;
  initialPaymentMethods?: IPaymentMethodUsage[];
  onPaymentMethodsChange: (methods: IPaymentMethodUsage[]) => void;
  disabled?: boolean;
  cashAccountId?: string;
  invoiceItems?: any[]; // عناصر الفاتورة للتحقق من وجود المنتج الخدمي المرتبط
}

const PaymentMethodsSection: React.FC<PaymentMethodsSectionProps> = ({
  invoiceTotal,
  initialPaymentMethods = [],
  onPaymentMethodsChange,
  disabled = false,
  cashAccountId,
  invoiceItems = []
}) => {
  // تدوير إجمالي الفاتورة إلى رقمين عشريين لتجنب أخطاء الفاصلة العائمة
  const roundedInvoiceTotal = Math.round((invoiceTotal || 0) * 100) / 100;

  const [availableMethods, setAvailableMethods] = useState<any[]>([]);
  const [selectedMethods, setSelectedMethods] = useState<IPaymentMethodUsage[]>(initialPaymentMethods);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [remainingAmount, setRemainingAmount] = useState(roundedInvoiceTotal);
  
  // استخدام useRef بدلاً من useState لتجنب إعادة render
  const pendingMethodIdRef = useRef<string>('');
  const pendingAmountRef = useRef<number>(0);

  // تحديث الطرق المختارة عند تغير القيم الاولية من الخارج
  useEffect(() => {
    console.log('تحديث طرق الدفع من القيم الأولية:', initialPaymentMethods);
    setSelectedMethods(initialPaymentMethods || []);
  }, [initialPaymentMethods]);
  const [showAddCashOption, setShowAddCashOption] = useState(false);
  
  // إعداد دالة التحقق من البيانات غير المحفوظة مرة واحدة فقط
  useEffect(() => {
    (window as any).__paymentMethodUnsavedCheck = () => ({
      hasUnsavedData: !!pendingMethodIdRef.current,
      selectedMethodId: pendingMethodIdRef.current,
      amount: pendingAmountRef.current
    });
    
    console.log('تم إعداد دالة التحقق من البيانات غير المحفوظة');
  }, []); // تشغيل مرة واحدة فقط
  
  // إجمالي المبالغ المدفوعة بجميع طرق الدفع
  const totalPaid = selectedMethods.reduce((sum, method) => sum + method.amount, 0);
  
  // حساب المبلغ المتبقي عند تغير إجمالي الفاتورة أو المدفوعات
  useEffect(() => {
    console.log('حساب المبلغ المتبقي - إجمالي الفاتورة:', roundedInvoiceTotal, 'المدفوع:', totalPaid);
    // استخدام القيمة المدورة لتجنب أخطاء الفاصلة العائمة
    const remaining = roundedInvoiceTotal - totalPaid;
    setRemainingAmount(remaining);
    
    // عرض خيار إضافة المبلغ المتبقي إلى الصندوق
    setShowAddCashOption(remaining > 0 && cashAccountId !== undefined);
    
    // التحقق من أن إجمالي المدفوعات لا يتجاوز إجمالي الفاتورة
    if (totalPaid > roundedInvoiceTotal) {
      setError('إجمالي المدفوعات أكبر من إجمالي الفاتورة');
    } else {
      setError(null);
    }
  }, [totalPaid, roundedInvoiceTotal, cashAccountId]);
  
  // جلب طرق الدفع المتاحة
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        setLoading(true);
        const methods = await paymentMethodService.getAuthorizedPaymentMethods();
        setAvailableMethods(methods.filter(method => method.isActive));
        setError(null);
      } catch (err) {
        console.error('Error fetching payment methods:', err);
        setError('حدث خطأ أثناء جلب طرق الدفع');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPaymentMethods();
  }, []);
  
  // حساب الرسوم
  const calculateFees = (amount: number, methodId: string): IFeesCalculation => {
    // البحث عن طريقة الدفع
    const method = availableMethods.find(m => m.id === methodId);
    
    if (!method) {
      return {
        fixedFees: 0,
        variableFees: 0,
        feesAmount: 0,
        taxAmount: 0,
        totalFees: 0
      };
    }
    
    // حساب الرسوم الثابتة والمتغيرة
    const fixedFees = method.fixedFees || 0;
    const variableFees = amount * (method.variableFees / 100) || 0;
    const feesAmount = fixedFees + variableFees;
    
    // حساب الضريبة على الرسوم
    const taxAmount = feesAmount * (method.taxPercentage / 100);
    
    // إجمالي الرسوم
    const totalFees = feesAmount + taxAmount;
    
    return {
      fixedFees,
      variableFees,
      feesAmount,
      taxAmount,
      totalFees
    };
  };
  // إضافة طريقة دفع
  const handleAddPaymentMethod = (methodId: string, amount: number) => {
    // التحقق من أن طريقة الدفع موجودة
    const method = availableMethods.find(m => m.id === methodId);
    if (!method) {
      console.error('طريقة الدفع غير موجودة:', methodId);
      setError('طريقة الدفع غير موجودة');
      return;
    }
    
    // التحقق من أن المبلغ صحيح
    if (amount <= 0) {
      console.error('المبلغ يجب أن يكون أكبر من صفر');
      setError('المبلغ يجب أن يكون أكبر من صفر');
      return;
    }
    
    // التحقق من وجود المنتج الخدمي المرتبط في جدول منتجات الفاتورة
    if (method.linkedServiceProductId) {
      const hasLinkedServiceProduct = invoiceItems.some(
        (item: any) => item.isServiceProduct && item.paymentMethodId === methodId
      );
      
      if (!hasLinkedServiceProduct) {
        console.error('المنتج الخدمي المرتبط بطريقة الدفع غير موجود في الفاتورة:', method.linkedServiceProductName || methodId);
        setError(`يجب إضافة المنتج الخدمي المرتبط بطريقة الدفع "${method.name || method.linkedServiceProductName || ''}" إلى الفاتورة أولاً`);
        return;
      }
    }
    
    // التحقق من أن المبلغ لا يتجاوز المبلغ المتبقي
    if (amount > roundedInvoiceTotal - totalPaid) {
      setError('المبلغ يتجاوز المبلغ المتبقي من الفاتورة');
      return;
    }
    
    // حساب الرسوم
    const fees = calculateFees(amount, methodId);
    
    // إنشاء كائن استخدام طريقة الدفع
    const newMethodUsage: IPaymentMethodUsage = {
      paymentMethodId: methodId,
      name: method.name,
      amount,
      feesAmount: fees.feesAmount,
      taxAmount: fees.taxAmount,
      totalFees: fees.totalFees,
      linkedAccountId: method.linkedAccountId
    };
    
    // إضافة طريقة الدفع إلى القائمة
    const updatedMethods = [...selectedMethods, newMethodUsage];
    
    console.log('إضافة طريقة دفع:', newMethodUsage);
    console.log('القائمة المحدثة:', updatedMethods);
    
    // تحديث الحالة المحلية
    setSelectedMethods(updatedMethods);
    
    // إرسال التغييرات إلى المكون الأب بطريقة آمنة
    setTimeout(() => {
      onPaymentMethodsChange(updatedMethods);
    }, 0);
  };
  
  // حذف طريقة دفع
  const handleRemovePaymentMethod = (index: number) => {
    const methodToRemove = selectedMethods[index];
    const updatedMethods = selectedMethods.filter((_, i) => i !== index);
    
    console.log('حذف طريقة دفع:', methodToRemove);
    console.log('القائمة المحدثة بعد الحذف:', updatedMethods);
    
    // تحديث الحالة المحلية
    setSelectedMethods(updatedMethods);
    
    // إرسال التغييرات إلى المكون الأب بطريقة آمنة
    setTimeout(() => {
      onPaymentMethodsChange(updatedMethods);
    }, 0);
  };
  
  // إضافة المبلغ المتبقي إلى الصندوق
  const handleAddRemainingToCash = () => {
    if (!cashAccountId || remainingAmount <= 0) return;
    
    // البحث عن طريقة دفع صندوق
    const cashMethod = {
      id: 'cash',
      name: 'نقداً (صندوق)',
      linkedAccountId: cashAccountId,
      fixedFees: 0,
      variableFees: 0,
      taxPercentage: 0
    };
    
    // إضافة طريقة دفع الصندوق
    const newMethodUsage: IPaymentMethodUsage = {
      paymentMethodId: 'cash',
      name: 'نقداً (صندوق)',
      amount: remainingAmount,
      feesAmount: 0,
      taxAmount: 0,
      totalFees: 0,
      linkedAccountId: cashAccountId
    };
    
    // إضافة طريقة الدفع إلى القائمة
    const updatedMethods = [...selectedMethods, newMethodUsage];
    
    console.log('إضافة المبلغ المتبقي للصندوق:', newMethodUsage);
    console.log('القائمة المحدثة:', updatedMethods);
    
    // تحديث الحالة المحلية
    setSelectedMethods(updatedMethods);
    
    // إرسال التغييرات إلى المكون الأب بطريقة آمنة
    setTimeout(() => {
      onPaymentMethodsChange(updatedMethods);
    }, 0);
  };
  
  // مكون إضافة طريقة دفع جديدة
  const PaymentMethodSelector = () => {
    const [selectedMethodId, setSelectedMethodId] = useState<string>('');
    const [amount, setAmount] = useState<number>((remainingAmount || 0) > 0 ? (remainingAmount || 0) : 0);
    const [amountInput, setAmountInput] = useState<string>(((remainingAmount || 0) > 0 ? (remainingAmount || 0) : 0).toString());
    
    // معالجة تغيير طريقة الدفع
    const handleMethodChange = (event: SelectChangeEvent<string>) => {
      const newMethodId = event.target.value;
      setSelectedMethodId(newMethodId);
      pendingMethodIdRef.current = newMethodId; // تحديث الـ ref بدون إعادة render
    };
    
    // معالجة تغيير المبلغ
    const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseFloat(event.target.value);
      const newAmount = isNaN(value) ? 0 : value;
      setAmount(newAmount);
      pendingAmountRef.current = newAmount; // تحديث الـ ref بدون إعادة render
    };
    
    // تقديم النموذج
    const handleSubmit = (event: React.FormEvent) => {
      event.preventDefault();
      if (selectedMethodId && amount > 0) {
        handleAddPaymentMethod(selectedMethodId, amount);
        // إعادة تعيين النموذج
        setSelectedMethodId('');
        setAmount(remainingAmount > 0 ? remainingAmount : 0);
        // مسح البيانات المعلقة من الـ ref
        pendingMethodIdRef.current = '';
        pendingAmountRef.current = 0;
      }
    };
    
    return (
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth disabled={disabled || availableMethods.length === 0}>
              <InputLabel id="payment-method-label">طريقة الدفع</InputLabel>
              <Select
                labelId="payment-method-label"
                value={selectedMethodId}
                onChange={handleMethodChange}
                label="طريقة الدفع"
              >
                {availableMethods.map(method => (
                  <MenuItem key={method.id} value={method.id}>
                    {method.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="المبلغ"
              type="number"
              value={amountInput}
              onChange={(e) => {
                const value = e.target.value;
                setAmountInput(value);
                
                // تحديث القيمة الرقمية فقط إذا كانت صالحة
                const numValue = parseFloat(value);
                if (!isNaN(numValue)) {
                  setAmount(numValue);
                  pendingAmountRef.current = numValue; // تحديث الـ ref بدون إعادة render
                } else if (value === '') {
                  setAmount(0);
                  pendingAmountRef.current = 0; // تحديث الـ ref بدون إعادة render
                }
              }}
              disabled={disabled || !selectedMethodId}
              InputProps={{
                endAdornment: <InputAdornment position="end">ريال</InputAdornment>,
                inputProps: { min: 0, step: 0.01, max: remainingAmount }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              type="submit"
              disabled={disabled || !selectedMethodId || amount <= 0 || amount > remainingAmount}
              startIcon={<AddIcon />}
            >
              إضافة
            </Button>
          </Grid>
        </Grid>
      </Box>
    );
  };
  
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6" display="flex" alignItems="center" gap={1}>
          <CalculateIcon color="primary" />
          طرق الدفع
        </Typography>
        
        <Chip
          label={`إجمالي الفاتورة: ${roundedInvoiceTotal.toFixed(2)} ريال`}
          color="primary"
          variant="outlined"
        />
      </Box>
      
      {loading && <CircularProgress size={24} sx={{ my: 2, mx: 'auto', display: 'block' }} />}
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {/* جدول طرق الدفع المختارة */}
      {selectedMethods.length > 0 && (
        <Box mb={3}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>طريقة الدفع</TableCell>
                  <TableCell align="center">المبلغ</TableCell>
                  <TableCell align="center">الرسوم</TableCell>
                  <TableCell align="center">الضريبة</TableCell>
                  <TableCell align="center">إجمالي الرسوم</TableCell>
                  <TableCell align="center">إجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedMethods.map((method, index) => (
                  <TableRow key={index}>
                    <TableCell>{method.name || 'Unknown'}</TableCell>
                    <TableCell align="center">{(method.amount || 0).toFixed(2)} ريال</TableCell>
                    <TableCell align="center">{(method.feesAmount || 0).toFixed(2)} ريال</TableCell>
                    <TableCell align="center">{(method.taxAmount || 0).toFixed(2)} ريال</TableCell>
                    <TableCell align="center">{(method.totalFees || 0).toFixed(2)} ريال</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemovePaymentMethod(index)}
                        disabled={disabled}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Box display="flex" justifyContent="space-between" mt={2}>
            <Chip
              label={`المدفوع: ${(totalPaid || 0).toFixed(2)} ريال`}
              color="success"
              variant="outlined"
              icon={<CheckCircleIcon />}
            />
            <Chip
              label={`المتبقي: ${Math.max(0, remainingAmount).toFixed(2)} ريال`}
              color={remainingAmount > 0 ? "warning" : "success"}
              variant="outlined"
              icon={remainingAmount > 0 ? <ErrorIcon /> : <CheckCircleIcon />}
            />
          </Box>
        </Box>
      )}
      
      <Divider sx={{ my: 2 }} />
      
      {/* إضافة طريقة دفع جديدة */}
      <Box mb={2}>
        <Typography variant="subtitle1" gutterBottom>
          إضافة طريقة دفع
        </Typography>
        <PaymentMethodSelector />
      </Box>
      
      
      {/* عرض رسالة توضيحية عند عدم وجود طرق دفع */}
      {!loading && availableMethods.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          لا توجد طرق دفع متاحة. يرجى إضافة طرق دفع في إعدادات طرق الدفع أولاً.
        </Alert>
      )}
    </Paper>
  );
};

export default PaymentMethodsSection;